import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MdClose,
  MdCloudUpload,
  MdDownload,
  MdSettings,
  MdUpload,
} from 'react-icons/md';
import examAPI from '../../api/examAPI';
import OdooAutocomplete from '../admissions/OdooAutocomplete';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

function Field({ label, children }) {
  return (
    <div className="ea-odoo-field">
      <label className="ea-odoo-field__label">{label}</label>
      <div className="ea-odoo-field__value">{children}</div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="ea-odoo-group">
      <h3 className="ea-odoo-group__title">{title}</h3>
      <div className="ea-odoo-group__body">{children}</div>
    </div>
  );
}

function downloadBase64(b64, filename, mimetype) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: mimetype });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function lineKey(line, idx) {
  return line.id || `tmp-${line.student_id || idx}`;
}

function withSelected(options, id, name) {
  if (!id) return options;
  if ((options || []).some((opt) => String(opt.id ?? opt.value) === String(id))) {
    return options;
  }
  return [{ id, name: name || '' }, ...(options || [])];
}

function markClass(total) {
  if (total >= 70) return 'ea-exam-marks__ok';
  if (total > 0 && total < 50) return 'ea-exam-marks__low';
  if (total === 0) return 'ea-exam-marks__muted';
  return '';
}

export default function ExamEntryFormPage() {
  const { entryId } = useParams();
  const isNew = !entryId || entryId === 'new';
  const navigate = useNavigate();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const importRef = useRef(null);

  const [facultyId, setFacultyId] = useState('');
  const [facultyName, setFacultyName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [departmentName, setDepartmentName] = useState('');
  const [semester, setSemester] = useState('1');
  const [batchId, setBatchId] = useState('');
  const [batchName, setBatchName] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [subjectName, setSubjectName] = useState('');
  const [lines, setLines] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const loadLookups = useCallback(async () => {
    const facRes = await fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.getFaculties(token)
    );
    if (facRes.error === 'no_token') {
      setError(HR_SESSION_ERROR);
      return;
    }
    if (facRes.error) {
      setError(facRes.error);
      return;
    }
    if (!facRes.error) setFaculties(facRes.data?.faculties || facRes.data?.records || []);
    const semRes = await fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.getSemesters(token)
    );
    if (!semRes.error) setSemesters(semRes.data?.semesters || semRes.data?.records || []);
  }, [ensureHrToken]);

  const loadDepartments = useCallback(
    async (facId) => {
      if (!facId) {
        setDepartments([]);
        return;
      }
      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
        examAPI.getDepartments(facId, token)
      );
      if (fetchError === 'no_token') {
        setError(HR_SESSION_ERROR);
        return;
      }
      setDepartments(data?.departments || data?.records || []);
    },
    [ensureHrToken]
  );

  const loadBatches = useCallback(
    async (depId) => {
      if (!depId) {
        setBatches([]);
        return;
      }
      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
        examAPI.getBatches(depId, token)
      );
      if (fetchError === 'no_token') {
        setError(HR_SESSION_ERROR);
        return;
      }
      setBatches(data?.batches || data?.records || []);
    },
    [ensureHrToken]
  );

  const loadSubjects = useCallback(
    async (facId, depId, sem, batId) => {
      if (!depId) {
        setSubjects([]);
        return;
      }
      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
        examAPI.getSubjects(
          {
            faculty_id: facId ? Number(facId) : undefined,
            department_id: Number(depId),
            semester: sem || undefined,
            batch_id: batId ? Number(batId) : undefined,
          },
          token
        )
      );
      if (fetchError === 'no_token') {
        setError(HR_SESSION_ERROR);
        return;
      }
      setSubjects(data?.subjects || data?.records || []);
    },
    [ensureHrToken]
  );

  const loadPreviewStudents = useCallback(async () => {
    if (!isNew || !batchId || !subjectId) {
      if (isNew && (!batchId || !subjectId)) setLines([]);
      return;
    }
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.previewStudents(
        {
          batch_id: Number(batchId),
          faculty_id: facultyId ? Number(facultyId) : undefined,
          department_id: departmentId ? Number(departmentId) : undefined,
          semester,
          subject_id: Number(subjectId),
        },
        token
      )
    );
    if (fetchError === 'no_token') {
      setError(HR_SESSION_ERROR);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      return;
    }
    setError(null);
    setLines(data.students || data.records || []);
  }, [ensureHrToken, isNew, batchId, subjectId, facultyId, departmentId, semester]);

  const loadRecord = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.getEntry({ entry_id: Number(entryId) }, token)
    );
    if (fetchError === 'no_token') {
      setError(HR_SESSION_ERROR);
      setLoading(false);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      setLoading(false);
      return;
    }
    const rec = data.record || data;
    setFacultyId(rec.faculty_id ? String(rec.faculty_id) : '');
    setFacultyName(rec.faculty_name || '');
    setDepartmentId(rec.department_id ? String(rec.department_id) : '');
    setDepartmentName(rec.department_name || '');
    setSemester(rec.semester || '1');
    setBatchId(rec.batch_id ? String(rec.batch_id) : '');
    setBatchName(rec.batch_name || '');
    setSubjectId(rec.subject_id ? String(rec.subject_id) : '');
    setSubjectName(rec.subject_name || '');
    setLines(rec.entry_lines || rec.lines || []);
    setLoading(false);
  }, [ensureHrToken, entryId, isNew]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  useEffect(() => {
    loadDepartments(facultyId);
  }, [facultyId, loadDepartments]);

  useEffect(() => {
    loadBatches(departmentId);
  }, [departmentId, loadBatches]);

  useEffect(() => {
    loadSubjects(facultyId, departmentId, semester, batchId);
  }, [facultyId, departmentId, semester, batchId, loadSubjects]);

  useEffect(() => {
    loadPreviewStudents();
  }, [loadPreviewStudents]);

  const setMark = (key, field, value) => {
    const cleaned = String(value).replace(/[^\d.]/g, '');
    setLines((prev) =>
      prev.map((line, idx) => {
        if (lineKey(line, idx) !== key) return line;
        const next = { ...line, [field]: cleaned };
        next.total_marks =
          (Number(next.midterm_marks) || 0) +
          (Number(next.final_marks) || 0) +
          (Number(next.oral_marks) || 0);
        return next;
      })
    );
  };

  const markValue = (value) =>
    value === '' || value === null || value === undefined ? '' : String(value);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    const linePayload = lines.map((line) => ({
      id: line.id || undefined,
      student_id: line.student_id,
      midterm_marks: Number(line.midterm_marks) || 0,
      final_marks: Number(line.final_marks) || 0,
      oral_marks: Number(line.oral_marks) || 0,
    }));

    if (isNew) {
      if (!facultyId || !departmentId || !semester || !batchId || !subjectId) {
        setError('Faculty, Department, Semester, Batch, and Subject are required.');
        setSaving(false);
        return;
      }
      const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
        examAPI.createEntry(
          {
            faculty_id: Number(facultyId),
            department_id: Number(departmentId),
            semester,
            batch_id: Number(batchId),
            subject_id: Number(subjectId),
            entry_lines: linePayload,
          },
          token
        )
      );
      setSaving(false);
      if (fetchError === 'no_token') {
        setError(HR_SESSION_ERROR);
        return;
      }
      if (fetchError) {
        setError(fetchError);
        return;
      }
      const createdId = data.record?.id;
      if (createdId) {
        navigate(`/bu-dashboard/exams/entries/${createdId}`, { replace: true });
      }
      return;
    }

    const { error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.updateEntry(
        {
          entry_id: Number(entryId),
          entry_lines: linePayload,
        },
        token
      )
    );
    setSaving(false);
    if (fetchError === 'no_token') {
      setError(HR_SESSION_ERROR);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      return;
    }
    loadRecord();
  };

  const onExport = async () => {
    if (isNew) {
      setError('Save the exam entry before exporting.');
      return;
    }
    setExporting(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      examAPI.exportEntry({ entry_id: Number(entryId), export_type: 'excel' }, token)
    );
    setExporting(false);
    if (fetchError === 'no_token') {
      setError(HR_SESSION_ERROR);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      return;
    }
    if (!data?.file_base64) {
      setError('Export file was empty.');
      return;
    }
    downloadBase64(
      data.file_base64,
      data.filename || 'Exam_Marks.xlsx',
      data.mimetype || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );
  };

  const headerLocked = !isNew;
  const facultyOptions = withSelected(faculties, facultyId, facultyName);
  const departmentOptions = withSelected(departments, departmentId, departmentName);
  const batchOptions = withSelected(batches, batchId, batchName);
  const subjectOptions = withSelected(
    subjects.map((s) => ({ id: s.id, name: s.name })),
    subjectId,
    subjectName
  );

  return (
    <div className="ea-odoo-form-page ea-adm-form ea-exam-form">
      <div className="ea-odoo-form-bar ea-adm-cp">
        <div className="ea-adm-cp__left">
          <button
            type="button"
            className="ea-odoo-new ea-adm-cp__new"
            onClick={() => navigate('/bu-dashboard/exams/entries/new')}
          >
            New
          </button>
          <div className="ea-adm-cp__title-block">
            <Link to="/bu-dashboard/exams/entries" className="ea-adm-cp__parent">
              Multi Exam Entry
            </Link>
            <div className="ea-adm-cp__record-row">
              <span className="ea-adm-cp__record">
                {isNew ? 'New' : subjectName || 'Exam entry'}
              </span>
              <button type="button" className="ea-adm-cp__icon" title="Actions" aria-label="Actions">
                <MdSettings size={16} />
              </button>
              <button
                type="button"
                className="ea-adm-cp__icon ea-adm-cp__icon--save"
                onClick={onSave}
                disabled={saving}
                title="Save"
                aria-label="Save"
              >
                <MdCloudUpload size={18} />
              </button>
              <button
                type="button"
                className="ea-adm-cp__icon"
                onClick={() => navigate('/bu-dashboard/exams/entries')}
                disabled={saving}
                title="Discard"
                aria-label="Discard"
              >
                <MdClose size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="ea-exam-form-header">
        <button
          type="button"
          className="ea-odoo-btn"
          onClick={() => importRef.current?.click()}
        >
          <MdUpload size={16} aria-hidden />
          Import Exam Data
        </button>
        <button
          type="button"
          className="ea-odoo-btn ea-odoo-btn--primary"
          onClick={onExport}
          disabled={exporting || isNew}
        >
          <MdDownload size={16} aria-hidden />
          {exporting ? 'Exporting…' : 'Export Report'}
        </button>
        <input ref={importRef} type="file" accept=".xls,.xlsx" hidden />
      </div>

      {error && (
        <div className="ea-odoo-alert ea-odoo-alert--error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p className="ea-hr-table__empty">Loading exam entry…</p>
      ) : (
        <div className="ea-odoo-sheet ea-exam-sheet">
          <div className="ea-odoo-sheet__body ea-exam-sheet__body">
            {!isNew && subjectName ? <h1 className="ea-exam-title">{subjectName}</h1> : null}

            <div className="ea-odoo-cols">
              <Group title="Academic Information">
                <Field label="Faculty">
                  <OdooAutocomplete
                    value={facultyId}
                    options={facultyOptions}
                    disabled={headerLocked}
                    placeholder=""
                    onChange={(val) => {
                      const found = facultyOptions.find((f) => String(f.id) === String(val));
                      setFacultyId(val);
                      setFacultyName(found?.name || '');
                      setDepartmentId('');
                      setDepartmentName('');
                      setBatchId('');
                      setBatchName('');
                      setSubjectId('');
                      setSubjectName('');
                      setLines([]);
                    }}
                  />
                </Field>
                <Field label="Department">
                  <OdooAutocomplete
                    value={departmentId}
                    options={departmentOptions}
                    disabled={headerLocked || !facultyId}
                    placeholder=""
                    onChange={(val) => {
                      const found = departmentOptions.find((d) => String(d.id) === String(val));
                      setDepartmentId(val);
                      setDepartmentName(found?.name || '');
                      setBatchId('');
                      setBatchName('');
                      setSubjectId('');
                      setSubjectName('');
                      setLines([]);
                    }}
                  />
                </Field>
                <Field label="Semester">
                  <OdooAutocomplete
                    value={semester}
                    options={semesters}
                    disabled={headerLocked}
                    placeholder=""
                    onChange={(val) => {
                      setSemester(val || '1');
                      setSubjectId('');
                      setSubjectName('');
                      setLines([]);
                    }}
                  />
                </Field>
              </Group>

              <Group title="Exam Information">
                <Field label="Batch">
                  <OdooAutocomplete
                    value={batchId}
                    options={batchOptions}
                    disabled={headerLocked || !departmentId}
                    placeholder=""
                    onChange={(val) => {
                      const found = batchOptions.find((b) => String(b.id) === String(val));
                      setBatchId(val);
                      setBatchName(found?.name || '');
                      setSubjectId('');
                      setSubjectName('');
                      setLines([]);
                    }}
                  />
                </Field>
                <Field label="Subject">
                  <OdooAutocomplete
                    value={subjectId}
                    options={subjectOptions}
                    disabled={headerLocked || !departmentId}
                    placeholder=""
                    onChange={(val) => {
                      setSubjectId(val);
                      const found = subjectOptions.find((s) => String(s.id) === String(val));
                      setSubjectName(found?.name || '');
                    }}
                  />
                </Field>
              </Group>
            </div>
          </div>

          <div className="ea-odoo-tabs ea-exam-tabs">
            <span className="ea-odoo-tabs__btn active">Exam Marks</span>
          </div>

          <div className="ea-exam-notebook__body">
            <div className="ea-odoo-list-wrap ea-exam-marks-wrap">
              <table className="ea-odoo-list ea-exam-marks">
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>SN</th>
                    <th>Student ID</th>
                    <th>Student</th>
                    <th>Midterm (30.0)</th>
                    <th>Final (30.0)</th>
                    <th>Oral (40.0)</th>
                    <th>Total Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => {
                    const key = lineKey(line, idx);
                    const total = Number(line.total_marks || 0);
                    return (
                      <tr key={key} className={markClass(total)}>
                        <td>{line.batch_name || batchName || ''}</td>
                        <td>{line.serial_no || idx + 1}</td>
                        <td className="ea-exam-student-no">{line.student_no || ''}</td>
                        <td>{line.student_name || ''}</td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            value={markValue(line.midterm_marks)}
                            onChange={(e) => setMark(key, 'midterm_marks', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            value={markValue(line.final_marks)}
                            onChange={(e) => setMark(key, 'final_marks', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            value={markValue(line.oral_marks)}
                            onChange={(e) => setMark(key, 'oral_marks', e.target.value)}
                          />
                        </td>
                        <td className="ea-exam-total">
                          {total.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button type="button" className="ea-exam-add-line" disabled>
                Add a line
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
